import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import {
  DocumentStatus,
  PostDraftInterface,
  PostReplyDraftInterfaceSliceState,
} from '../interfaces/post.interface';
import type { TextBlock } from '../interfaces/textEditor.interface';

const textBlocksInitialState: TextBlock = {
  id: '',
  type: undefined,
  data: {
    paragraph: '',
    header: '',
    level: 0,
    items: [],
    file: undefined,
    code: '',
    withBorder: false,
    withBackground: false,
    stretched: false,
    caption: '',
    style: 'unordered',
  },
};

const draftInitialState: PostDraftInterface = {
  hasStoryAnalysis: false,
  storyAnalysis: [],
  id: '',
  parentPostId: '',
  status: DocumentStatus.DRAFT,
  categories: [],
  textBlocks: [],
  article: true,
  textBlocksVersions: [],
  username: '',
  blocks: [],
};

const draftStoreInitialState: PostReplyDraftInterfaceSliceState = {
  postReplyDrafts: [],
  postReplyDraft: draftInitialState,
  categories: [],
  addDraft: false,
  textBlocks: [textBlocksInitialState],
  createDraft: false,
  editDraft: false,
  isEditingPostReplyDraft: false,
  isAddingDraft: false,
  isAddingPostReplyAsDraft: false,
  saveDraftStart: false,
  saveDraftSuccess: false,
  refreshPostReplyDraft: false,
  publishDraft: false,
  publishPostReplyInit: false,
  publishPostReplySuccess: false,
  updatedPostDraft: {},
  deletedDraftId: '',
  savedImageKey: [],
  reAnalyzeStory: false,
  feedPostId: undefined,
};

export const postReplyDraftSlice = createSlice({
  name: 'postReplyDraftStore',
  initialState: draftStoreInitialState,
  reducers: {
    GET_POST_REPLY_DRAFT_STORE_ACTION: (state, action: PayloadAction<any>) => {
      state.postReplyDraft = action.payload;
    },
    ADD_POST_REPLY_DRAFT_STORE_ACTION: (state, action: PayloadAction<any>) => {
      state.postReplyDraft = action.payload;
    },
    IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isAddingPostReplyAsDraft = action.payload;
    },
    FEED_POST_ID: (state, action: PayloadAction<string | undefined>) => {
      state.feedPostId = action.payload;
    },
    SAVE_POST_DRAFT: (state, action: PayloadAction<boolean>) => {
      // Before a post get's published it is saved in draft mode.
      state.createDraft = action.payload;
    },
    SAVE_DRAFT_START_STORE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.saveDraftStart = action.payload;
    },
    SAVE_DRAFT_SUCCESS_STORE_ACTION: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.saveDraftSuccess = action.payload;
    },
    EDITED_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<PostDraftInterface>,
    ) => {
      state.updatedPostDraft = action.payload;
    },
    REFRESH_POST_REPLY_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<any>,
    ) => {
      state.refreshPostReplyDraft = action.payload;
    },
    EDIT_POST_DRAFT: (state) => {
      state.editDraft = true;
    },
    IS_EDITING_POST_REPLY_DRAFT_STORE_ACTION: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.isEditingPostReplyDraft = action.payload;
    },
    ADD_POST_REPLY_TEXT_BLOCKS: (state, action: PayloadAction<TextBlock[]>) => {
      state.textBlocks = action.payload;
    },
    RESET_POST_REPLY_DRAFT_STATE: (state, action: PayloadAction<boolean>) => {
      state.postReplyDraft = draftInitialState;
    },
    SAVE_POST_REPLY_DRAFT_CATEGORIES: (
      state,
      action: PayloadAction<string[]>,
    ) => {
      // (state.categories as string[]) = action.payload;
      state.postReplyDraft.categories = action.payload;
    },
    RESET_POST_REPLY_DRAFT_CATEGORIES: (state) => {
      (state.categories as string[]) = [];
    },
    RESET_POST_REPLY_UPDATED_DRAFT: (state) => {
      state.updatedPostDraft = {};
    },
    POPULATE_CATEGORIES: (state, action: PayloadAction<string[]>) => {
      (state.categories as string[]) = action.payload;
    },
    PUBLISH_POST_REPLY_INIT: (state, action: PayloadAction<boolean>) => {
      state.publishPostReplyInit = action.payload;
    },
    PUBLISH_POST_REPLY_SUCCESS: (state, action: PayloadAction<boolean>) => {
      state.publishPostReplySuccess = action.payload;
    },
    SET_EDITED_DRAFT: (state, action: PayloadAction<PostDraftInterface>) => {
      //TODO: remove this action after testing is no longer needed
      state.updatedPostDraft = action.payload;
    },
    SAVED_IMAGE_KEY: (state, action: PayloadAction<string>) => {
      // @ts-ignore
      state.savedImageKey.push(action.payload);
    },
    CLEAR_IMAGE_KEY: (state, action: PayloadAction<boolean>) => {
      // @ts-ignore
      state.savedImageKey = [];
    },
    RESET_IMAGE_KEYS: (state, action: PayloadAction<string[]>) => {
      state.savedImageKey = [];
      // @ts-ignore
      state.savedImageKey = action.payload;
    },
    SET_RE_ANALYZE_ACTION: (state, action: PayloadAction<boolean>) => {
      state.reAnalyzeStory = action.payload;
    },
  },
});

export default postReplyDraftSlice.reducer;
